<template>
  <b-modal
    :id="id"
    size="xl"
    dialog-class="group-preview-dialog"
    centered
    scrollable
    lazy
    hide-footer
    title-class="group-preview__title"
    :title="title"
  >
    <p class="group-preview__note">
      The accounts this group's automation would message right now. Click a row
      to open the customer in a new tab.
    </p>

    <p v-if="error" class="group-preview__error">{{ error }}</p>

    <FilterTable
      class="group-preview__table"
      :fields="fields"
      :callback="callback"
      data-cy="group-preview-table"
      @handleClick="openCustomer"
    />
  </b-modal>
</template>

<script lang="ts">
import FilterTable from '@/components/Table/FilterTable.vue'
import { toErrorList } from '@/utils/apiErrors'
import endpoints from '@/utils/endpoints.js'
import { cfAxios } from '@/utils/init-axios.js'
import {
  DEFAULT_HIDDEN_COLUMN_KEYS,
  baseFields
} from '@/views/Customers/CustomerListView/customerColumns'
import moment from 'moment'
import { Component, Prop, Vue } from 'vue-property-decorator'
import { mapGetters } from 'vuex'

/** A preview row: core-service returns the Customers list's row shape for the group's audience. */
interface PreviewRow {
  customer: { id: string }
  account: { due_date?: string | null }
  _rowVariant?: string
}

@Component({
  components: { FilterTable },
  computed: { ...mapGetters(['currentMerchant']) }
})
export default class GroupPreviewModal extends Vue {
  @Prop({ type: String, default: 'group-preview-modal' }) id!: string
  /** The group filter being built; previewed as it stands when the modal opens. */
  @Prop({ type: Object, required: true }) filter!: Record<string, unknown>

  currentMerchant!: { id: string; pkid: number }

  count: number | null = null
  error: string | null = null

  // The Customers list's default columns, sorted the same way; no selection checkbox here.
  get fields() {
    return baseFields.filter(
      field =>
        field.key !== 'checkbox' &&
        !DEFAULT_HIDDEN_COLUMN_KEYS.includes(field.key)
    )
  }

  get title(): string {
    if (this.count === null) return 'Accounts in this group'

    return `${this.count.toLocaleString()} account${this.count === 1 ? '' : 's'} in this group`
  }

  /** FilterTable's loader: core's group preview, with FilterTable's ordering and paging. */
  // A method, not an arrow property: Vue binds methods to the component; class arrows miss it.
  async callback(params: Record<string, unknown>) {
    this.error = null
    try {
      const response = await cfAxios.post(
        endpoints.customerGroupsPreview(this.currentMerchant.pkid),
        {
          filter: this.filter,
          ordering: params.ordering,
          page: params.page,
          page_size: params.page_size
        }
      )
      this.count = response.data?.count ?? 0
      response.data.results = (response.data?.results ?? []).map(
        (row: PreviewRow) => {
          // The Customers list tints a past-due row the same way.
          const due = row.account?.due_date
          if (due && moment().diff(moment(due), 'days') > 0) {
            row._rowVariant = 'danger'
          }

          return row
        }
      )

      return response
    } catch (err) {
      this.count = 0
      this.error = toErrorList(
        (err as { response?: { data?: unknown } })?.response?.data,
        'Failed to load the accounts.'
      )[0]

      return { count: 0, data: { count: 0, results: [] } }
    }
  }

  customerLink(row: PreviewRow) {
    return {
      name: 'CustomerAccountDetails',
      params: {
        merchantID: this.currentMerchant.id,
        customerID: row.customer.id
      }
    }
  }

  // A new tab, so the group being built is not lost.
  openCustomer(row: PreviewRow) {
    if (!row?.customer?.id) return
    window.open(this.$router.resolve(this.customerLink(row)).href, '_blank')
  }
}
</script>

<style lang="scss" scoped>
.group-preview__note {
  margin-bottom: 12px;
  color: $darker-gray;

  font-size: 14px;
}

.group-preview__error {
  color: $red;
}

.group-preview__table {
  min-height: 200px;
}
</style>

<style lang="scss">
// b-modal renders outside this component, so its size cannot be scoped. Two classes, to outrank
// Bootstrap's .modal-xl max-width. A compact table keeps every Customers column in a modest window.
.modal-dialog.group-preview-dialog {
  max-width: min(1280px, 85vw);

  .modal-content {
    max-height: calc(100vh - 48px);
  }

  table {
    font-size: 13px;

    th,
    td {
      padding: 0.45rem 0.5rem;
    }
  }

  // Table.vue hides its scrollbar; here a too-wide table must visibly scroll, not look cut off.
  .table-content-container {
    overflow-x: auto;
    scrollbar-width: auto;

    &::-webkit-scrollbar {
      display: block;
      height: 10px;
    }

    &::-webkit-scrollbar-thumb {
      background: rgb(0 0 0 / 25%);
      border-radius: 5px;
    }
  }
}
</style>
